import { useMemo, useState } from "react";
import { api, fmtTRY, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, ArrowDownRight, ArrowRightLeft, Trash2, Receipt, Search, X, SearchX } from "lucide-react";

const typeMeta = {
  gelir: { label: "Gelir", icon: ArrowUpRight, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  gider: { label: "Gider", icon: ArrowDownRight, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  transfer: { label: "Transfer", icon: ArrowRightLeft, cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

export default function TransactionsSection({ transactions, accounts, onChange, canEdit = false }) {
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const accMap = useMemo(() => {
    const m = {};
    accounts.forEach((a) => (m[a.id] = a));
    return m;
  }, [accounts]);

  const hasFilters = q.trim() || start || end || typeFilter !== "all";

  const filtered = useMemo(() => {
    const query = q.trim().toLocaleLowerCase("tr-TR");
    return transactions.filter((t) => {
      if (typeFilter !== "all" && t.type !== typeFilter) return false;
      if (start && t.date < start) return false;
      if (end && t.date > end) return false;
      if (query) {
        const acc = accMap[t.account_id];
        const toAcc = accMap[t.to_account_id];
        const hay = `${t.description || ""} ${acc?.name || ""} ${acc?.bank_name || ""} ${toAcc?.name || ""}`.toLocaleLowerCase("tr-TR");
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [transactions, q, start, end, typeFilter, accMap]);

  const stats = useMemo(() => {
    let gelir = 0, gider = 0;
    filtered.forEach((t) => {
      if (t.type === "gelir") gelir += Number(t.amount);
      else if (t.type === "gider") gider += Number(t.amount);
    });
    return { gelir, gider };
  }, [filtered]);

  const clearFilters = () => {
    setQ("");
    setStart("");
    setEnd("");
    setTypeFilter("all");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("İşlem silindi");
      onChange();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const noneAtAll = transactions.length === 0;

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
          <div className="lg:col-span-5 space-y-1.5">
            <Label>Ara</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="tx-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder='Örn. "kira", "maaş", banka adı...'
                className="pl-9"
              />
            </div>
          </div>
          <div className="lg:col-span-2 space-y-1.5">
            <Label>Tür</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="tx-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="gelir">Gelir</SelectItem>
                <SelectItem value="gider">Gider</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2 space-y-1.5">
            <Label>Başlangıç</Label>
            <Input
              data-testid="tx-filter-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="lg:col-span-2 space-y-1.5">
            <Label>Bitiş</Label>
            <Input
              data-testid="tx-filter-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
          <div className="lg:col-span-1">
            <Button
              data-testid="tx-clear-filters"
              variant="outline"
              onClick={clearFilters}
              disabled={!hasFilters}
              className="w-full gap-1 border-slate-200 text-slate-600"
              title="Filtreleri temizle"
            >
              <X className="h-4 w-4" /> Temizle
            </Button>
          </div>
        </div>

        {hasFilters && (
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="tx-filter-summary">
            <span className="text-slate-500">
              <span className="font-semibold text-slate-900">{filtered.length}</span> sonuç
            </span>
            <span className="text-emerald-600">Gelir: <span className="font-semibold">{fmtTRY(stats.gelir)}</span></span>
            <span className="text-rose-600">Gider: <span className="font-semibold">{fmtTRY(stats.gider)}</span></span>
          </div>
        )}
      </div>

      {/* Table / empty states */}
      {noneAtAll ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Receipt className="h-6 w-6 text-slate-400" />
          </div>
          <p className="text-slate-900 font-semibold">Henüz bir işlem bulunmuyor</p>
          <p className="text-sm text-slate-500 mt-1">Yeni bir gelir/gider ekleyerek başlayın.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="tx-no-results">
          <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <SearchX className="h-6 w-6 text-slate-400" />
          </div>
          <p className="text-slate-900 font-semibold">Sonuç bulunamadı</p>
          <p className="text-sm text-slate-500 mt-1">Arama veya tarih filtrelerinizi değiştirmeyi deneyin.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <Table data-testid="transactions-table">
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Tür</TableHead>
                <TableHead>Açıklama</TableHead>
                <TableHead>Hesap</TableHead>
                <TableHead>Tarih</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                {canEdit && <TableHead className="w-10"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((t) => {
                const meta = typeMeta[t.type] || typeMeta.gelir;
                const Icon = meta.icon;
                const acc = accMap[t.account_id];
                const toAcc = accMap[t.to_account_id];
                const sign = t.type === "gelir" ? "+" : t.type === "gider" ? "-" : "";
                const amtColor =
                  t.type === "gelir"
                    ? "text-emerald-600"
                    : t.type === "gider"
                    ? "text-rose-600"
                    : "text-blue-600";
                return (
                  <TableRow key={t.id} data-testid={`tx-row-${t.id}`} className="hover:bg-slate-50/70 transition-colors">
                    <TableCell>
                      <Badge variant="outline" className={`gap-1 ${meta.cls}`}>
                        <Icon className="h-3 w-3" /> {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[220px] truncate text-slate-700">
                      {t.description || <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {t.type === "transfer" ? (
                        <span>
                          {acc?.name || "?"} → {toAcc?.name || "?"}
                        </span>
                      ) : (
                        acc?.name || "?"
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(t.date)}</TableCell>
                    <TableCell className={`text-right font-semibold tabular-nums ${amtColor}`}>
                      {sign}
                      {fmtTRY(t.amount)}
                    </TableCell>
                    {canEdit && (
                    <TableCell>
                      <Button
                        data-testid={`delete-tx-${t.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(t.id)}
                        className="h-8 w-8 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
